import { useState, useCallback } from 'react';
import { toast } from '../components/toast';

const STATIC_USERS = [
  {
    id: 1,
    name: 'John Smith',
    email: 'john.smith@company.com',
    assignedBucket: 'Shared Documents Bucket',
  },
  {
    id: 2,
    name: 'Sarah Johnson',
    email: 'sarah.johnson@company.com',
    assignedBucket: 'HR Documents Bucket',
  },
  {
    id: 3,
    name: 'Mike Wilson',
    email: 'mike.wilson@client.com',
    assignedBucket: 'Client Files Bucket',
  },
  {
    id: 4,
    name: 'Emily Davis',
    email: 'emily.davis@company.com',
    assignedBucket: null,
  },
  {
    id: 5,
    name: 'Robert Brown',
    email: 'robert.brown@client.com',
    assignedBucket: 'Project Files Bucket',
  },
];

const STATIC_BUCKETS = [
  {
    id: 1,
    name: 'Shared Documents Bucket',
    path: '/storage/shared/documents',
    size: '2.5 GB',
    assignedUser: 'John Smith',
    createdDate: '2024-01-15',
  },
  {
    id: 2,
    name: 'Client Files Bucket',
    path: '/storage/clients/files',
    size: '5.2 GB',
    assignedUser: 'Mike Wilson',
    createdDate: '2024-01-20',
  },
  {
    id: 3,
    name: 'HR Documents Bucket',
    path: '/storage/hr/documents',
    size: '1.8 GB',
    assignedUser: 'Sarah Johnson',
    createdDate: '2024-02-01',
  },
  {
    id: 4,
    name: 'Finance Reports Bucket',
    path: '/storage/finance/reports',
    size: '3.1 GB',
    assignedUser: null,
    createdDate: '2024-02-10',
  },
  {
    id: 5,
    name: 'Project Files Bucket',
    path: '/storage/projects/files',
    size: '7.3 GB',
    assignedUser: 'Robert Brown',
    createdDate: '2024-02-15',
  },
];

export const useStorageOperations = () => {
  const [users, setUsers] = useState(STATIC_USERS);
  const [buckets, setBuckets] = useState(STATIC_BUCKETS);
  const [loading, setLoading] = useState(false);

  const createBucket = useCallback(
    async (bucketData) => {
      setLoading(true);
      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const assignedUser = users.find((u) => u.id === bucketData.assigned_user);
        const newBucket = {
          id: buckets.length + 1,
          name: bucketData.name,
          path: bucketData.path,
          size: '0 GB',
          assignedUser: assignedUser ? assignedUser.name : null,
          createdDate: new Date().toISOString().split('T')[0],
          status: 'Active',
        };

        setBuckets((prevBuckets) => [...prevBuckets, newBucket]);

        // Update user's assigned bucket
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === bucketData.assigned_user ? { ...user, assignedBucket: bucketData.name } : user
          )
        );

        toast.success('Bucket created successfully');
        return { success: true, data: newBucket };
      } catch (error) {
        toast.error('Failed to create bucket');
        return { success: false, error: error.message };
      } finally {
        setLoading(false);
      }
    },
    [users, buckets]
  );

  const updateBucket = useCallback(async (bucketId, bucketData) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setBuckets((prevBuckets) =>
        prevBuckets.map((bucket) => (bucket.id === bucketId ? { ...bucket, ...bucketData } : bucket))
      );

      toast.success('Bucket updated successfully');
      return { success: true };
    } catch (error) {
      toast.error('Failed to update bucket');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteBucket = useCallback(
    async (bucketId) => {
      setLoading(true);
      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const bucketToDelete = buckets.find((b) => b.id === bucketId);

        setBuckets((prevBuckets) => prevBuckets.filter((bucket) => bucket.id !== bucketId));

        // Remove bucket assignment from user
        if (bucketToDelete?.assignedUser) {
          setUsers((prevUsers) =>
            prevUsers.map((user) =>
              user.name === bucketToDelete.assignedUser ? { ...user, assignedBucket: null } : user
            )
          );
        }

        toast.success('Bucket deleted successfully');
        return { success: true };
      } catch (error) {
        toast.error('Failed to delete bucket');
        return { success: false, error: error.message };
      } finally {
        setLoading(false);
      }
    },
    [buckets]
  );

  const assignUserToBucket = useCallback(
    async (userId, bucketId) => {
      setLoading(true);
      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const user = users.find((u) => u.id === userId);
        const bucket = buckets.find((b) => b.id === bucketId);

        if (!user || !bucket) {
          throw new Error('User or bucket not found');
        }

        // Update bucket assignment
        setBuckets((prevBuckets) =>
          prevBuckets.map((b) => (b.id === bucketId ? { ...b, assignedUser: user.name } : b))
        );

        // Update user's assigned bucket
        setUsers((prevUsers) => prevUsers.map((u) => (u.id === userId ? { ...u, assignedBucket: bucket.name } : u)));

        toast.success('User assigned to bucket successfully');
        return { success: true };
      } catch (error) {
        toast.error('Failed to assign user to bucket');
        return { success: false, error: error.message };
      } finally {
        setLoading(false);
      }
    },
    [users, buckets]
  );

  const unassignUserFromBucket = useCallback(
    async (bucketId) => {
      setLoading(true);
      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const bucket = buckets.find((b) => b.id === bucketId);

        if (!bucket) {
          throw new Error('Bucket not found');
        }

        // Remove bucket assignment
        setBuckets((prevBuckets) => prevBuckets.map((b) => (b.id === bucketId ? { ...b, assignedUser: null } : b)));

        // Remove bucket assignment from user
        if (bucket.assignedUser) {
          setUsers((prevUsers) =>
            prevUsers.map((user) => (user.name === bucket.assignedUser ? { ...user, assignedBucket: null } : user))
          );
        }

        toast.success('User unassigned from bucket successfully');
        return { success: true };
      } catch (error) {
        toast.error('Failed to unassign user from bucket');
        return { success: false, error: error.message };
      } finally {
        setLoading(false);
      }
    },
    [buckets]
  );

  const getStorageStats = useCallback(() => {
    const totalUsers = users.length;
    const totalBuckets = buckets.length;
    const assignedBuckets = buckets.filter((b) => b.assignedUser).length;

    // Calculate total storage (simplified calculation)
    const totalStorage = buckets.reduce((total, bucket) => {
      const sizeInGB = parseFloat(bucket.size);
      return total + sizeInGB;
    }, 0);

    return {
      totalUsers,
      totalBuckets,
      assignedBuckets,
      totalStorage: `${totalStorage.toFixed(1)} GB`,
    };
  }, [users, buckets]);

  return {
    users,
    buckets,
    loading,
    createBucket,
    updateBucket,
    deleteBucket,
    assignUserToBucket,
    unassignUserFromBucket,
    getStorageStats,
  };
};
